import { useEffect, useRef, RefObject } from "react";
import classnamesBind from "classnames/bind";
import Icon from "../icon/Icon";
import { SMALL_VIEWPORT } from "../../constants";
import { useViewport } from "../../context/viewport/ViewportContext";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { useLiveRegion } from "../../context/liveRegion/LiveRegionContext";
import useKeyboardZoom from "../../hooks/useKeyboardZoom";
import styles from "./KeyboardZoomControls.scss";

const classnames = classnamesBind.bind(styles);

interface KeyboardZoomControlsProps {
  containerRef: RefObject<HTMLDivElement>;
  isZoomed: boolean;
  currentScale: number;
  currentScaleRef: RefObject<number>;
  positionXRef: RefObject<number>;
  positionYRef: RefObject<number>;
  contentWidth: number;
  contentHeight: number;
  zoomScaleLevels: number[];
  isInactive?: boolean;
  onClickZoom: (position: { clientX: number; clientY: number }) => void;
  onPan: (x: number, y: number) => void;
}

export default function KeyboardZoomControls({
  containerRef,
  isZoomed,
  currentScale,
  currentScaleRef,
  positionXRef,
  positionYRef,
  contentWidth,
  contentHeight,
  zoomScaleLevels,
  isInactive = false,
  onClickZoom,
  onPan,
}: KeyboardZoomControlsProps) {
  const { viewportCategory } = useViewport() as {
    viewportCategory: string;
  };
  const { getTranslation } = useRuntimeConstants() as {
    getTranslation: (key: string) => string;
  };
  const { updateLiveRegion } = useLiveRegion() as {
    updateLiveRegion: (text: string) => void;
  };

  const keyboard = useKeyboardZoom({
    containerRef,
    isZoomed,
    currentScaleRef,
    positionXRef,
    positionYRef,
    contentWidth,
    contentHeight,
    onClickZoom,
    onPan,
  });

  const previousScaleRef = useRef<number | null>(null);

  useEffect(() => {
    const previousScale = previousScaleRef.current;
    previousScaleRef.current = currentScale;

    if (
      previousScale === null ||
      previousScale === currentScale ||
      isInactive
    ) {
      return;
    }

    const maxScale = zoomScaleLevels[zoomScaleLevels.length - 1];
    let announcement: string;

    if (currentScale >= maxScale) {
      announcement = getTranslation("pdp_media_gallery_zoom_four_hundred");
    } else if (currentScale >= zoomScaleLevels[0]) {
      announcement = getTranslation("pdp_media_gallery_zoom_two_hundred");
    } else {
      announcement = getTranslation("pdp_media_gallery_zoom_one_hundred");
    }

    updateLiveRegion(announcement);
  }, [
    currentScale,
    getTranslation,
    isInactive,
    updateLiveRegion,
    zoomScaleLevels,
  ]);

  if (viewportCategory === SMALL_VIEWPORT) {
    return null;
  }

  return (
    <>
      <button
        className={styles.zoomButton}
        aria-label={getTranslation("pdp_media_gallery_zoom_hint")}
        aria-hidden={isInactive || undefined}
        tabIndex={isInactive ? -1 : undefined}
        onClick={(event) => {
          event.stopPropagation();
          keyboard.handleZoomClick();
        }}
        onPointerDown={(event) => event.stopPropagation()}
        onKeyDown={keyboard.handleKeyDown}
        onKeyUp={keyboard.handleKeyUp}
        data-testid="keyboardZoomControls"
      >
        <Icon
          id={
            currentScale >= zoomScaleLevels[zoomScaleLevels.length - 1]
              ? "zoom-minus"
              : "zoom-plus"
          }
          className={styles.zoomIcon}
        />
      </button>
      <p
        aria-hidden="true"
        className={classnames(styles.zoomHint, {
          showHint: keyboard.isFocused,
        })}
      >
        {getTranslation("pdp_media_gallery_zoom_hint")}
      </p>
    </>
  );
}
